import { firebase, db } from '@/firebase.js'
import { DateTime } from 'luxon'
// import { firestoreAction } from 'vuexfire'

const getDefaultState = () => {
  return {
    allStoreLogs: [],
    detailedTaskData: [],
    storeDetails: {},
    userDetails: {},
    allUsers: [],
    isLoading: false
  }
}

const state = getDefaultState()

const getters = {
  allStoreLogs: (state) => state.allStoreLogs,
  detailedTaskData: (state) => state.detailedTaskData,
  storeDetails: (state) => state.storeDetails,
  userDetails: (state) => state.userDetails,
  allUsers: (state) => state.allUsers,
  isLoading: (state) => state.isLoading,

  // Helper getter to get store name by ID
  storeNameById: (state) => (storeId) => {
    if (state.storeDetails[storeId] && state.storeDetails[storeId].name) {
      return state.storeDetails[storeId].name
    }
    return storeId // Fallback to ID if name not found
  },

  // Helper getter to get user name by ID
  userNameById: (state) => (userId) => {
    if (state.userDetails[userId] && state.userDetails[userId].displayName) {
      return state.userDetails[userId].displayName
    }
    return userId // Fallback to ID if name not found
  }
}

const mutations = {
  setAllStoreLogs(state, logs) {
    state.allStoreLogs = logs
  },
  setLoading(state, isLoading) {
    state.isLoading = isLoading
  },
  resetState(state) {
    Object.assign(state, getDefaultState())
  },
  setDetailedTaskData(state, data) {
    state.detailedTaskData = data
  },
  setStoreDetails(state, storeDetails) {
    state.storeDetails = storeDetails
  },
  setUserDetails(state, userDetails) {
    state.userDetails = userDetails
  },
  setAllUsers(state, users) {
    state.allUsers = users
  },
  updateUserInList(state, { id, data }) {
    const idx = state.allUsers.findIndex((u) => u.id === id)
    if (idx !== -1) {
      state.allUsers.splice(idx, 1, { ...state.allUsers[idx], ...data })
    }
  },
  addUserToList(state, user) {
    state.allUsers.push(user)
  }
}

const actions = {
  // Fetch store details
  async fetchStoreDetails({ commit }) {
    try {
      const storesRef = db.collection('stores')
      const snapshot = await storesRef.get()

      const storeDetails = {}
      snapshot.forEach((doc) => {
        const storeData = doc.data()
        storeDetails[doc.id] = {
          id: doc.id,
          name: storeData.name || doc.id,
          address: storeData.address || '',
          storeCode: storeData.storeCode || '',
          timezone: storeData.timezone || 'America/New_York'
        }
      })

      commit('setStoreDetails', storeDetails)
      return storeDetails
    } catch (error) {
      console.error('Error fetching store details:', error)
      return {}
    }
  },

  // Fetch all users (auth + Firestore) via Cloud Function — developer/admin only
  async fetchAllUsers({ commit }) {
    commit('setLoading', true)
    try {
      const fn = firebase.functions().httpsCallable('listUsers')
      const result = await fn()
      commit('setAllUsers', result.data.users || [])
      return result.data.users || []
    } catch (error) {
      console.error('Error fetching all users:', error)
      return []
    } finally {
      commit('setLoading', false)
    }
  },

  // Create a new Firebase Auth + Firestore user via Cloud Function
  async adminCreateUser({ commit }, userData) {
    const fn = firebase.functions().httpsCallable('adminCreateUser')
    const result = await fn(userData)
    commit('addUserToList', { id: result.data.uid, ...result.data })
    return result.data
  },

  // Update a user's Firestore doc and optionally their Auth record via Cloud Function
  async adminUpdateUser({ commit }, { userId, firestoreData, authData }) {
    const fn = firebase.functions().httpsCallable('adminUpdateUser')
    await fn({
      userId,
      firestoreData: firestoreData || {},
      authData: authData || {}
    })
    if (firestoreData) {
      commit('updateUserInList', { id: userId, data: firestoreData })
    }
    if (
      authData &&
      Object.prototype.hasOwnProperty.call(authData, 'disabled')
    ) {
      commit('updateUserInList', {
        id: userId,
        data: { disabled: authData.disabled }
      })
    }
  },

  // Fetch user details
  async fetchUserDetails({ commit }) {
    try {
      const usersRef = db.collection('users')
      const snapshot = await usersRef.get()

      const userDetails = {}
      snapshot.forEach((doc) => {
        const userData = doc.data()
        userDetails[doc.id] = {
          id: doc.id,
          displayName: userData.displayName || userData.name || doc.id,
          email: userData.email || '',
          role: userData.role || ''
        }
      })

      commit('setUserDetails', userDetails)
      return userDetails
    } catch (error) {
      console.error('Error fetching user details:', error)
      return {}
    }
  },

  // Enhanced fetchAllStoreLogs to handle daily tasks stored as individual properties
  async fetchAllStoreLogs({ commit, getters, dispatch }, dateRangeOrDays = 14) {
    commit('setLoading', true)
    try {
      // First ensure we have store details
      if (Object.keys(getters.storeDetails).length === 0) {
        await dispatch('fetchStoreDetails')
      }

      // Handle both old (days) and new (date range) parameter formats with improved timezone handling
      let startDate, endDate
      if (
        typeof dateRangeOrDays === 'object' &&
        dateRangeOrDays.startDate &&
        dateRangeOrDays.endDate
      ) {
        // New format: { startDate, endDate }
        // Use UTC to ensure consistent date handling across timezones
        startDate = DateTime.fromJSDate(dateRangeOrDays.startDate)
          .toUTC()
          .startOf('day')
          .toJSDate()
        endDate = DateTime.fromJSDate(dateRangeOrDays.endDate)
          .toUTC()
          .endOf('day')
          .toJSDate()
      } else {
        // Old format: number of days (for backward compatibility)
        const days = typeof dateRangeOrDays === 'number' ? dateRangeOrDays : 14
        startDate = DateTime.now()
          .toUTC()
          .minus({ days })
          .startOf('day')
          .toJSDate()
        endDate = DateTime.now().toUTC().endOf('day').toJSDate()
      }

      console.log(
        `Fetching logs from ${DateTime.fromJSDate(
          startDate
        ).toISO()} to ${DateTime.fromJSDate(endDate).toISO()}`
      )

      const stores = getters.storeDetails

      // Only fetch from stores we have details for
      const storeIds = Object.keys(stores)

      if (!storeIds.length) {
        commit('setAllStoreLogs', [])
        return []
      }

      const allLogs = []

      // Fetch logs from each store
      await Promise.all(
        storeIds.map(async (storeId) => {
          // Get the proper store name from our store details
          const storeName = getters.storeNameById(storeId)

          const logsRef = db
            .collection('stores')
            .doc(storeId)
            .collection('logs')
            .where('date', '>=', startDate)
            .where('date', '<=', endDate)

          const snapshot = await logsRef.get()

          snapshot.forEach((doc) => {
            const logData = doc.data()

            // Process tasks to include task details and count completed tasks
            const tasks = []
            let completedTasksCount = 0
            let totalTasksCount = 0

            // For daily logs, tasks are stored as individual properties
            if (!logData.type || logData.type === 'daily') {
              // Find properties that look like tasks (have 'completed' field)
              const taskProperties = Object.entries(logData).filter(
                ([key, value]) =>
                  typeof value === 'object' &&
                  value !== null &&
                  !Array.isArray(value) &&
                  'completed' in value &&
                  key !== 'date' &&
                  key !== 'createdAt' &&
                  key !== 'tasks' // Skip the empty tasks array
              )

              totalTasksCount = taskProperties.length

              taskProperties.forEach(([taskId, taskData]) => {
                const isCompleted = taskData.completed === true

                if (isCompleted) {
                  completedTasksCount++
                }

                tasks.push({
                  id: taskId,
                  name: taskData.name || taskId,
                  completed: isCompleted,
                  completedAt: taskData.completedAt || null,
                  completedBy:
                    taskData.completedByName || taskData.completedBy || null
                })
              })
            }

            // For weekly/monthly logs, check each task ID directly
            if (logData.type === 'weekly' || logData.type === 'monthly') {
              // Get all task IDs that are in this log
              const taskIds = Object.keys(logData).filter(
                (key) =>
                  key !== 'id' &&
                  key !== 'type' &&
                  key !== 'date' &&
                  key !== 'completed' &&
                  key !== 'storeId' &&
                  key !== 'createdAt' &&
                  key !== 'status'
              )

              totalTasksCount = taskIds.length

              taskIds.forEach((taskId) => {
                const taskData = logData[taskId]
                const isCompleted = taskData && taskData.completed === true

                if (isCompleted) {
                  completedTasksCount++
                }

                tasks.push({
                  id: taskId,
                  name: taskData && taskData.name ? taskData.name : taskId,
                  completed: isCompleted,
                  completedAt:
                    taskData && taskData.completedAt
                      ? taskData.completedAt
                      : null,
                  completedBy:
                    taskData && taskData.completedByName
                      ? taskData.completedByName
                      : taskData && taskData.completedBy
                      ? taskData.completedBy
                      : null
                })
              })
            }

            // Calculate completion percentage
            const completionPercentage =
              totalTasksCount > 0
                ? Math.round((completedTasksCount / totalTasksCount) * 100)
                : 0

            // Determine if the log is completed (100% completion)
            const isCompleted = completionPercentage === 100

            console.log(
              `Log ${
                doc.id
              } for store ${storeName}: ${completedTasksCount}/${totalTasksCount} tasks completed (${completionPercentage}%) - ${
                isCompleted ? 'COMPLETED' : 'INCOMPLETE'
              }`
            )

            allLogs.push({
              id: doc.id,
              ...logData,
              storeId,
              storeName,
              tasks,
              completedTasksCount,
              totalTasksCount,
              completionPercentage,
              completed: isCompleted // Add the completed property
            })
          })
        })
      )

      // Sort by date (newest first)
      const sortedLogs = allLogs.sort((a, b) => {
        const dateA = a.date && a.date.seconds ? a.date.seconds : 0
        const dateB = b.date && b.date.seconds ? b.date.seconds : 0
        return dateB - dateA
      })

      commit('setAllStoreLogs', sortedLogs)
      return sortedLogs
    } catch (error) {
      console.error('Error fetching all store logs:', error)
      commit('setAllStoreLogs', [])
      return []
    } finally {
      commit('setLoading', false)
    }
  },

  // Fixed fetchDetailedTaskData to handle daily tasks stored as individual properties
  async fetchDetailedTaskData(
    { commit, getters, dispatch },
    dateRangeOrDays = 14
  ) {
    commit('setLoading', true)
    try {
      // First ensure we have store and user details
      if (Object.keys(getters.storeDetails).length === 0) {
        await dispatch('fetchStoreDetails')
      }

      if (Object.keys(getters.userDetails).length === 0) {
        await dispatch('fetchUserDetails')
      }

      // Handle both old (days) and new (date range) parameter formats with improved timezone handling
      let startDate, endDate
      if (
        typeof dateRangeOrDays === 'object' &&
        dateRangeOrDays.startDate &&
        dateRangeOrDays.endDate
      ) {
        // New format: { startDate, endDate }
        // Use UTC to ensure consistent date handling across timezones
        startDate = DateTime.fromJSDate(dateRangeOrDays.startDate)
          .toUTC()
          .startOf('day')
          .toJSDate()
        endDate = DateTime.fromJSDate(dateRangeOrDays.endDate)
          .toUTC()
          .endOf('day')
          .toJSDate()
      } else {
        // Old format: number of days (for backward compatibility)
        const days = typeof dateRangeOrDays === 'number' ? dateRangeOrDays : 14
        startDate = DateTime.now()
          .toUTC()
          .minus({ days })
          .startOf('day')
          .toJSDate()
        endDate = DateTime.now().toUTC().endOf('day').toJSDate()
      }

      console.log(
        `Fetching detailed tasks from ${DateTime.fromJSDate(
          startDate
        ).toISO()} to ${DateTime.fromJSDate(endDate).toISO()}`
      )

      const storeIds = Object.keys(getters.storeDetails)

      if (!storeIds.length) {
        commit('setDetailedTaskData', [])
        return []
      }

      const allTasks = []

      await Promise.all(
        storeIds.map(async (storeId) => {
          const storeName = getters.storeNameById(storeId)

          const logsRef = db
            .collection('stores')
            .doc(storeId)
            .collection('logs')
            .where('date', '>=', startDate)
            .where('date', '<=', endDate)

          const snapshot = await logsRef.get()

          snapshot.forEach((doc) => {
            const logData = doc.data()
            const logId = doc.id
            const logDate = logData.date
            // If type is not specified, default to 'daily'
            const logType = logData.type || 'daily'

            // For daily logs, look for task properties directly on the log object
            if (logType === 'daily') {
              // Find properties that look like tasks (have 'completed' field)
              const taskProperties = Object.entries(logData).filter(
                ([key, value]) =>
                  typeof value === 'object' &&
                  value !== null &&
                  !Array.isArray(value) &&
                  'completed' in value &&
                  key !== 'date' &&
                  key !== 'createdAt' &&
                  key !== 'tasks' // Skip the empty tasks array
              )

              console.log(
                `Daily log ${logId} has ${taskProperties.length} task-like properties`
              )

              // Process each task-like property
              taskProperties.forEach(([taskId, taskData]) => {
                // Get user name if we have a user ID
                let completedBy = null
                if (taskData.completedByName) {
                  completedBy = taskData.completedByName
                } else if (taskData.completedBy) {
                  completedBy = getters.userNameById(taskData.completedBy)
                }

                allTasks.push({
                  id: `${logId}-${taskId}`,
                  taskId,
                  logId,
                  storeId,
                  storeName,
                  type: 'daily',
                  name: taskData.name || taskId,
                  date: logDate,
                  completed: taskData.completed || false,
                  completedAt: taskData.completedAt || null,
                  completedBy: completedBy,
                  formattedDate:
                    logDate && logDate.seconds
                      ? DateTime.fromSeconds(logDate.seconds).toFormat(
                          'yyyy-MM-dd'
                        )
                      : 'Unknown'
                })
              })
            }

            // For weekly/monthly logs, process tasks as before
            if (logType === 'weekly' || logType === 'monthly') {
              // Get all task IDs that are in this log (exclude metadata fields)
              const taskIds = Object.keys(logData).filter(
                (key) =>
                  key !== 'id' &&
                  key !== 'type' &&
                  key !== 'date' &&
                  key !== 'completed' &&
                  key !== 'storeId' &&
                  key !== 'createdAt' &&
                  key !== 'status'
              )

              taskIds.forEach((taskId) => {
                const taskData = logData[taskId]
                if (!taskData) return

                // Get user name if we have a user ID
                let completedBy = null
                if (taskData.completedByName) {
                  completedBy = taskData.completedByName
                } else if (taskData.completedBy) {
                  completedBy = getters.userNameById(taskData.completedBy)
                }

                allTasks.push({
                  id: `${logId}-${taskId}`,
                  taskId,
                  logId,
                  storeId,
                  storeName,
                  type: logType,
                  name: taskData.name || taskId,
                  date: logDate,
                  completed: taskData.completed || false,
                  completedAt: taskData.completedAt || null,
                  completedBy: completedBy,
                  formattedDate:
                    logDate && logDate.seconds
                      ? DateTime.fromSeconds(logDate.seconds).toFormat(
                          'yyyy-MM-dd'
                        )
                      : 'Unknown'
                })
              })
            }
          })
        })
      )

      // Add debug logging to see what we've collected
      console.log(`Total tasks collected: ${allTasks.length}`)
      const dailyTasks = allTasks.filter((task) => task.type === 'daily')
      console.log(`Daily tasks: ${dailyTasks.length}`)
      if (dailyTasks.length > 0) {
        console.log('Sample daily task:', dailyTasks[0])
      }
      const weeklyTasks = allTasks.filter((task) => task.type === 'weekly')
      console.log(`Weekly tasks: ${weeklyTasks.length}`)
      const monthlyTasks = allTasks.filter((task) => task.type === 'monthly')
      console.log(`Monthly tasks: ${monthlyTasks.length}`)

      // Sort by date (newest first) and then by store name
      const sortedTasks = allTasks.sort((a, b) => {
        // First sort by date
        const dateA = a.date && a.date.seconds ? a.date.seconds : 0
        const dateB = b.date && b.date.seconds ? b.date.seconds : 0
        if (dateB !== dateA) return dateB - dateA

        // Then by store name
        return a.storeName.localeCompare(b.storeName)
      })

      commit('setDetailedTaskData', sortedTasks)
      return sortedTasks
    } catch (error) {
      console.error('Error fetching detailed task data:', error)
      commit('setDetailedTaskData', [])
      return []
    } finally {
      commit('setLoading', false)
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
